import React, { useCallback, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PersonIcon from '@mui/icons-material/Person'
import PaymentIcon from '@mui/icons-material/Payment'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto'
import CloseIcon from '@mui/icons-material/Close'
import type { IBlockPayloadForm } from '../../../types'
import SelectSearch from '../../../../../components/SelectSearch'
import useAxios from '../../../../../hooks/useAxios'
import type { IAddressThailand } from '../../../../../types/address'
import type { IOptionSelect } from '../../../../../components/SelectSearch'


type PaymentMethod = 'COD' | 'BANK'

interface OrderItem {
  productId: string
  qty: number
}

export const FormRenderer: React.FC<{ payload: IBlockPayloadForm }> = ({ payload }) => {
  const products = payload.products ?? []

  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [subDistrict, setSubDistrict] = useState('')
  const [district, setDistrict] = useState('')
  const [province, setProvince] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('COD')
  const [slipPreview, setSlipPreview] = useState<string | null>(null)
  const [slipName, setSlipName] = useState('')
  const [slipSize, setSlipSize] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = useCallback((file: File | undefined) => {
    if (!file) return
    if (!file.type.startsWith('image/')) return
    setSlipName(file.name)
    setSlipSize(file.size)
    const url = URL.createObjectURL(file)
    setSlipPreview(url)
  }, [])

  const clearSlip = useCallback(() => {
    if (slipPreview) URL.revokeObjectURL(slipPreview)
    setSlipPreview(null)
    setSlipName('')
    setSlipSize(0)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [slipPreview])

  const [searchAddress, setSearchAddress] = useState('')
  const [resetAutoComplete, setResetAutoComplete] = useState('')

  const addressList = useAxios<IAddressThailand[]>({
    method: 'GET',
    url: '/address/thailand-address',
    params: {
      page: 1,
      perPage: 5,
      search: searchAddress,
      startDate: '',
      endDate: '',
    },
  })

  const handleSearchAddress = async (value: string) => {
    setSearchAddress(value)
    await addressList.mutate?.()
  }

  const handleSelectAddress = (option: IOptionSelect<IAddressThailand> | null) => {
    if (!option) return
    const addr = option.value
    setSubDistrict(addr.subDistrict)
    setDistrict(addr.district)
    setProvince(addr.province)
    setPostalCode(addr.zipcode)
    setResetAutoComplete(Date.now().toString())
  }

  const productMap = useMemo(
    () => new Map(products.map((p) => [p.objectId, p])),
    [products],
  )

  const handleAddItem = (productId: string) => {
    if (!productId) return
    const existing = orderItems.find((i) => i.productId === productId)
    if (existing) {
      setOrderItems((prev) =>
        prev.map((i) => (i.productId === productId ? { ...i, qty: i.qty + 1 } : i)),
      )
    } else {
      setOrderItems((prev) => [...prev, { productId, qty: 1 }])
    }
  }

  const handleQty = (productId: string, delta: number) => {
    setOrderItems((prev) =>
      prev
        .map((i) => (i.productId === productId ? { ...i, qty: Math.max(0, i.qty + delta) } : i))
        .filter((i) => i.qty > 0),
    )
  }

  const handleRemoveItem = (productId: string) => {
    setOrderItems((prev) => prev.filter((i) => i.productId !== productId))
  }

  const totalPrice = useMemo(
    () =>
      orderItems.reduce((sum, item) => {
        const prod = productMap.get(item.productId)
        return sum + (prod ? prod.price * item.qty : 0)
      }, 0),
    [orderItems, productMap],
  )

  if (products.length === 0) return null

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'text-bottom' }} />
          {payload.btnText || 'สั่งซื้อเลย'}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
          กรอกข้อมูลด้านล่างเพื่อทำการสั่งซื้อ
        </Typography>
      </Box>

      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Product Selection */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ShoppingCartIcon sx={{ fontSize: 18 }} />
            เลือกสินค้า
          </Typography>

          <TextField
            select
            fullWidth
            label="เลือกสินค้าที่ต้องการ"
            value=""
            onChange={(e) => handleAddItem(e.target.value)}
          >
            {products.map((prod) => (
              <MenuItem key={prod.objectId} value={prod.objectId}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  {prod.image && (
                    <Box
                      component="img"
                      src={prod.image}
                      alt={prod.name}
                      sx={{ width: 32, height: 32, borderRadius: 0.5, objectFit: 'cover' }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{prod.name}</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    ฿{prod.price?.toLocaleString()}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* Selected Items */}
          {orderItems.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {orderItems.map((item) => {
                const prod = productMap.get(item.productId)
                if (!prod) return null
                const lineTotal = prod.price * item.qty
                return (
                  <Paper
                    key={item.productId}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    {prod.image && (
                      <Box
                        component="img"
                        src={prod.image}
                        alt={prod.name}
                        sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover' }}
                      />
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{prod.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ฿{prod.price?.toLocaleString()} × {item.qty} = <b>฿{lineTotal.toLocaleString()}</b>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleQty(item.productId, -1)}
                        sx={{ border: '1px solid', borderColor: 'divider', width: 28, height: 28 }}
                      >
                        <RemoveIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ minWidth: 28, textAlign: 'center' }}
                      >
                        {item.qty}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleQty(item.productId, 1)}
                        sx={{ border: '1px solid', borderColor: 'divider', width: 28, height: 28 }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveItem(item.productId)}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Paper>
                )
              })}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Customer Info */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon sx={{ fontSize: 18 }} />
            ข้อมูลผู้สั่งซื้อ
          </Typography>
          <TextField
            fullWidth
            label="ชื่อ-นามสกุล"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Box>

        {/* Shipping Address */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocalShippingIcon sx={{ fontSize: 18 }} />
            ที่อยู่จัดส่ง
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="ที่อยู่"
              multiline
              rows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <SelectSearch<IAddressThailand>
              resetAutoComplete={resetAutoComplete}
              label="ค้นหาที่อยู่ (ตำบล/อำเภอ/จังหวัด/รหัสไปรษณีย์)"
              onSelect={handleSelectAddress}
              search={searchAddress}
              onSearch={handleSearchAddress}
              loading={addressList.isLoading}
              options={
                addressList.data?.map((addr: IAddressThailand) => ({
                  label: `${addr.subDistrict} ${addr.district} ${addr.province} ${addr.zipcode}`,
                  value: addr,
                })) ?? []
              }
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="ตำบล/แขวง"
                  value={subDistrict}
                  onChange={(e) => setSubDistrict(e.target.value)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="อำเภอ/เขต"
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="จังหวัด"
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="รหัสไปรษณีย์"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  inputProps={{ maxLength: 5, inputMode: 'numeric' }}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Divider />

        {/* Payment Method */}
        <Box>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PaymentIcon sx={{ fontSize: 18 }} />
            วิธีชำระเงิน
          </Typography>
          <ToggleButtonGroup
            value={paymentMethod}
            exclusive
            onChange={(_, v) => { if (v) setPaymentMethod(v as PaymentMethod) }}
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value="COD" sx={{ textTransform: 'none', fontWeight: 600 }}>
              <LocalShippingIcon sx={{ mr: 1, fontSize: 20 }} />
              เก็บเงินปลายทาง
            </ToggleButton>
            <ToggleButton value="BANK" sx={{ textTransform: 'none', fontWeight: 600 }}>
              <PaymentIcon sx={{ mr: 1, fontSize: 20 }} />
              ชำระเงินแล้ว
            </ToggleButton>
          </ToggleButtonGroup>

          {paymentMethod === 'BANK' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                อัพโหลดหลักฐานการโอนเงิน (สลิป)
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              {!slipPreview ? (
                <Paper
                  variant="outlined"
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]) }}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderColor: dragOver ? 'primary.main' : 'divider',
                    bgcolor: dragOver ? 'rgba(102, 126, 234, 0.08)' : 'rgba(0,0,0,0.02)',
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(102, 126, 234, 0.04)',
                    },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, color: dragOver ? 'primary.main' : 'text.disabled' }} />
                  <Typography variant="body2" fontWeight={600} color={dragOver ? 'primary.main' : 'text.secondary'}>
                    ลากรูปมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    รองรับ JPG, PNG, GIF (สูงสุด 5 MB)
                  </Typography>
                </Paper>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 2 }}
                >
                  <Box
                    component="img"
                    src={slipPreview}
                    alt="สลิป"
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      <InsertPhotoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      {slipName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(slipSize / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                  <IconButton size="small" color="error" onClick={clearSlip}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Paper>
              )}
            </Box>
          )}
        </Box>

        <Divider />

        {/* Summary & Submit */}
        <Box>
          {orderItems.length > 0 && (
            <Paper
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                bgcolor: 'rgba(102, 126, 234, 0.06)',
                border: '1px solid rgba(102, 126, 234, 0.2)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  สินค้า {orderItems.reduce((s, i) => s + i.qty, 0)} ชิ้น
                </Typography>
                <Typography variant="h5" fontWeight={800} color="primary.main">
                  ฿{totalPrice.toLocaleString()}
                </Typography>
              </Box>
            </Paper>
          )}
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={orderItems.length === 0 || !name.trim()}
            sx={{
              py: 1.5,
              fontSize: 16,
              fontWeight: 700,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%)',
              },
            }}
          >
            {payload.btnText || 'สั่งซื้อเลย'}
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
